import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ThemeToggle from '../shared/ThemeToggle';

interface SearchItem {
  label: string;
  path: string;
  icon: string;
  type: 'Calculator' | 'Tool' | 'Guide';
  keywords: string;
}

// Lifted from Header.tsx — same service index, maintained together
function useServiceIndex(): SearchItem[] {
  return useMemo(() => [
    { label: 'Blob Storage', path: '/blob-storage', icon: 'folder_open', type: 'Calculator', keywords: 'blob storage hot cool cold archive storage account' },
    { label: 'Logic Apps', path: '/logic-apps', icon: 'account_tree', type: 'Calculator', keywords: 'logic apps integration workflow connectors standard consumption' },
    { label: 'Azure Functions', path: '/functions', icon: 'bolt', type: 'Calculator', keywords: 'functions function flex consumption premium serverless compute' },
    { label: 'App Service', path: '/app-service', icon: 'web', type: 'Calculator', keywords: 'app service web apps linux windows premium v3 v4' },
    { label: 'Bandwidth & Egress', path: '/bandwidth', icon: 'swap_horiz', type: 'Calculator', keywords: 'bandwidth egress outbound transfer inter-region internet' },
    { label: 'Azure Firewall', path: '/firewall', icon: 'shield', type: 'Calculator', keywords: 'firewall azure firewall basic standard premium logging' },
    { label: 'Log Analytics', path: '/log-analytics', icon: 'monitor_heart', type: 'Calculator', keywords: 'log analytics sentinel azure monitor ingestion retention search restore' },
    { label: 'Networking', path: '/networking', icon: 'hub', type: 'Calculator', keywords: 'networking nat gateway vnet peering public ip ddos virtual network' },
    { label: 'Data Factory', path: '/data-factory', icon: 'schema', type: 'Calculator', keywords: 'data factory adf copy activity data flow integration runtime' },
    { label: 'Azure Virtual Desktop', path: '/virtual-desktop', icon: 'desktop_windows', type: 'Calculator', keywords: 'virtual desktop avd fslogix session host' },
    { label: 'Azure Front Door', path: '/front-door', icon: 'public', type: 'Calculator', keywords: 'front door cdn waf edge premium standard' },
    { label: 'SQL Database', path: '/sql-database', icon: 'database', type: 'Calculator', keywords: 'sql database azure sql dtu vcore elastic pool' },
    { label: 'SQL Managed Instance', path: '/sql-managed-instance', icon: 'database', type: 'Calculator', keywords: 'sql managed instance sqlmi vcore general purpose business critical' },
    { label: 'Azure OpenAI', path: '/azure-openai', icon: 'smart_toy', type: 'Calculator', keywords: 'openai gpt azure ai foundry tokens ptu provisioned gpt-4o' },
    { label: 'Azure Managed Disks', path: '/managed-disks', icon: 'storage', type: 'Calculator', keywords: 'managed disks hdd ssd premium ultra storage iops' },
    { label: 'Key Vault', path: '/key-vault', icon: 'lock', type: 'Calculator', keywords: 'key vault hsm secrets certificates operations' },
    { label: 'Azure DevOps', path: '/azure-devops', icon: 'code', type: 'Calculator', keywords: 'azure devops pipelines parallel jobs ci cd artifacts' },
    { label: 'Site Recovery', path: '/site-recovery', icon: 'cloud_sync', type: 'Calculator', keywords: 'site recovery asr disaster recovery dr failover replication' },
    { label: 'Azure Kubernetes Service', path: '/aks', icon: 'lan', type: 'Calculator', keywords: 'aks kubernetes k8s container node pool spot uptime sla' },
    { label: 'Application Gateway', path: '/application-gateway', icon: 'call_split', type: 'Calculator', keywords: 'application gateway waf v2 standard autoscale capacity units' },
    { label: 'KQL Query Builder', path: '/kql-builder', icon: 'terminal', type: 'Tool', keywords: 'kql query builder tool logs queries analytics' },
    { label: 'Guided Estimator', path: '/guided', icon: 'route', type: 'Tool', keywords: 'guided estimator walkthrough wizard recommendation' },
    { label: 'Methodology', path: '/methodology', icon: 'science', type: 'Guide', keywords: 'methodology how prices work pricing data api formulas' },
    { label: 'Azure Billing Traps UK', path: '/guides/azure-billing-traps-uk', icon: 'warning', type: 'Guide', keywords: 'billing traps gotchas hidden costs guide' },
    { label: 'Changelog', path: '/changelog', icon: 'history', type: 'Guide', keywords: 'changelog updates sprint release notes' },
  ], []);
}

interface PageHeaderProps {
  onMobileMenuToggle: () => void;
}

export default function PageHeader({ onMobileMenuToggle }: PageHeaderProps) {
  const navigate = useNavigate();
  const serviceIndex = useServiceIndex();
  const [serviceQuery, setServiceQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const searchResults = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    return serviceIndex
      .map((item) => {
        const label = item.label.toLowerCase();
        const keywords = item.keywords.toLowerCase();
        const labelStarts = label.startsWith(q);
        const labelIncludes = label.includes(q);
        const keywordIncludes = keywords.includes(q);
        if (!labelStarts && !labelIncludes && !keywordIncludes) return null;
        const rank = labelStarts ? 0 : labelIncludes ? 1 : 2;
        return { item, rank };
      })
      .filter((e): e is { item: SearchItem; rank: number } => e !== null)
      .sort((a, b) => a.rank - b.rank || a.item.label.localeCompare(b.item.label))
      .slice(0, 6)
      .map((e) => e.item);
  }, [serviceIndex, serviceQuery]);

  useEffect(() => {
    setActiveIndex(searchResults.length > 0 ? 0 : -1);
  }, [searchResults]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setActiveIndex(-1);
        setMobileSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectResult = (item: SearchItem) => {
    navigate(item.path);
    setServiceQuery('');
    setActiveIndex(-1);
    setMobileSearchOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hit = activeIndex >= 0 ? searchResults[activeIndex] : searchResults[0];
    if (hit) selectResult(hit);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((p) => (p + 1) % searchResults.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((p) => (p <= 0 ? searchResults.length - 1 : p - 1)); }
    if (e.key === 'Enter' && activeIndex >= 0) { e.preventDefault(); selectResult(searchResults[activeIndex]); }
    if (e.key === 'Escape') { e.preventDefault(); setActiveIndex(-1); setMobileSearchOpen(false); }
  };

  const showDropdown = searchResults.length > 0 && activeIndex >= 0 && serviceQuery.trim().length >= 2;

  return (
    <div
      className="sticky top-0 z-40 flex items-center justify-between px-4 md:px-6 gap-3"
      style={{
        height: '48px',
        backgroundColor: 'var(--header-bg)',
        borderBottom: '1px solid var(--outline-variant)',
        transition: 'background-color 0.2s ease',
      }}
    >
      {/* Mobile: hamburger */}
      <button
        onClick={onMobileMenuToggle}
        className="material-symbols-outlined p-1 rounded transition-all md:hidden shrink-0"
        style={{ color: 'var(--text-primary)' }}
        aria-label="Open navigation menu"
      >
        menu
      </button>

      {/* Search — desktop always visible, mobile as icon */}
      <div className="flex-1 flex justify-start" ref={searchRef}>
        {/* Desktop search */}
        <form onSubmit={handleSubmit} className="hidden sm:block relative">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-[18px]">search</span>
          <input
            value={serviceQuery}
            onChange={(e) => setServiceQuery(e.target.value)}
            onFocus={() => { if (searchResults.length > 0) setActiveIndex(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search services..."
            className="w-[240px] rounded-sm py-1.5 pl-10 pr-3 text-sm placeholder:text-outline/50 focus:outline-none focus:border-[#0078d4]"
            style={{
              border: '1px solid var(--outline-variant)',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-primary)',
            }}
          />
          {showDropdown && (
            <SearchDropdown results={searchResults} activeIndex={activeIndex} onSelect={selectResult} onHover={setActiveIndex} />
          )}
        </form>

        {/* Mobile search icon + inline input */}
        <div className="sm:hidden relative">
          {mobileSearchOpen ? (
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <input
                autoFocus
                value={serviceQuery}
                onChange={(e) => setServiceQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="w-[160px] rounded-sm py-1 px-2 text-sm focus:outline-none"
                style={{ border: '1px solid var(--outline-variant)', backgroundColor: 'var(--input-bg)', color: 'var(--text-primary)' }}
              />
              {showDropdown && (
                <SearchDropdown results={searchResults} activeIndex={activeIndex} onSelect={selectResult} onHover={setActiveIndex} />
              )}
            </form>
          ) : (
            <button
              onClick={() => setMobileSearchOpen(true)}
              className="material-symbols-outlined p-1 rounded"
              style={{ color: 'var(--text-secondary)' }}
              aria-label="Search"
            >
              search
            </button>
          )}
        </div>
      </div>

      <ThemeToggle />
    </div>
  );
}

function SearchDropdown({ results, activeIndex, onSelect, onHover }: {
  results: SearchItem[];
  activeIndex: number;
  onSelect: (item: SearchItem) => void;
  onHover: (i: number) => void;
}) {
  return (
    <div
      className="absolute top-[calc(100%+6px)] left-0 w-[280px] rounded-sm overflow-hidden"
      style={{
        backgroundColor: 'var(--surface-container-high)',
        border: '1px solid var(--outline-variant)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 90,
      }}
    >
      {results.map((result, index) => (
        <button
          key={result.path}
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onSelect(result)}
          onMouseEnter={() => onHover(index)}
          className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors"
          style={{ backgroundColor: index === activeIndex ? 'var(--surface-container-highest)' : 'var(--surface-container-high)' }}
        >
          <span className="material-symbols-outlined text-[18px] shrink-0" style={{ color: '#0078d4' }}>{result.icon}</span>
          <span className="flex-1 text-sm" style={{ color: 'var(--text-primary)' }}>{result.label}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ color: 'var(--text-tertiary)', backgroundColor: 'var(--surface-container)' }}>
            {result.type}
          </span>
        </button>
      ))}
    </div>
  );
}
